// frontend/src/contexts/OrganizationContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { organizationAPI } from '../services/api';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const OrganizationContext = createContext({});

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  // authLoading matters as much as isAuthenticated here. On a page load
  // AuthContext starts with isAuthenticated=false and loading=true while it
  // validates the stored token, so "false" means "not known yet", not
  // "logged out". Treating those as the same thing wiped the saved
  // organization on every refresh — see the initial-load effect below.
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [membership, setMembership] = useState(null);
  const [usage, setUsage] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user's organizations
  const fetchOrganizations = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await organizationAPI.getUserOrganizations();
      // API returns { success, data: { organizations } } so we access response.data.data.organizations
      const responseData = response.data?.data || response.data;
      const orgs = responseData?.organizations || [];
      setOrganizations(orgs);

      // Set or update current organization
      if (orgs.length > 0) {
        const primary = orgs.find(org => org.membership?.isPrimary) || orgs[0];
        // Get current org ID from localStorage to check if we should update
        const currentOrgId = localStorage.getItem('currentOrganizationId');
        const existingOrg = currentOrgId ? orgs.find(o => o.id === currentOrgId) : null;

        if (existingOrg) {
          // Update current organization with fresh data (e.g., after tier change)
          setCurrentOrganization(existingOrg);
          setMembership(existingOrg.membership);
        } else {
          // No valid current organization, use primary
          setCurrentOrganization(primary);
          setMembership(primary.membership);
          localStorage.setItem('currentOrganizationId', primary.id);

          // Only surface this when a previously-selected org actually went
          // away (membership revoked, org deactivated) — not on a first
          // sign-in, where there is simply nothing saved yet. Switching a
          // user's organization without telling them is how work ends up
          // filed under the wrong tenant.
          if (currentOrgId) {
            toast(`Your previous organization is no longer available. Switched to ${primary.name}.`, {
              icon: 'ℹ️',
            });
          }
        }
      } else {
        setCurrentOrganization(null);
        setMembership(null);
        localStorage.removeItem('currentOrganizationId');
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch current organization details
  const fetchOrganizationDetails = useCallback(async () => {
    if (!currentOrganization?.id) return;

    try {
      const response = await organizationAPI.getOrganization();
      // API returns { success, data: { organization, tier, usage, memberCount } }
      const responseData = response.data?.data || response.data;
      const { organization, tier, usage: orgUsage, memberCount } = responseData;

      setTierInfo(tier);
      setUsage(orgUsage);

      // Update current organization with full details
      setCurrentOrganization(prev => ({
        ...prev,
        ...organization,
        memberCount
      }));
    } catch (err) {
      console.error('Error fetching organization details:', err);
    }
  }, [currentOrganization?.id]);

  // Switch to a different organization
  const switchOrganization = useCallback(async (organizationId) => {
    const org = organizations.find(o => o.id === organizationId);
    if (!org) {
      toast.error('Organization not found');
      return false;
    }

    try {
      setCurrentOrganization(org);
      setMembership(org.membership);
      localStorage.setItem('currentOrganizationId', organizationId);

      // Fetch fresh details for the new organization
      await fetchOrganizationDetails();

      toast.success(`Switched to ${org.name}`);
      return true;
    } catch (err) {
      console.error('Error switching organization:', err);
      toast.error('Failed to switch organization');
      return false;
    }
  }, [organizations, fetchOrganizationDetails]);

  // Set primary organization
  const setPrimaryOrganization = useCallback(async (organizationId) => {
    try {
      await organizationAPI.setPrimaryOrganization(organizationId);
      await fetchOrganizations();
      toast.success('Primary organization updated');
      return true;
    } catch (err) {
      console.error('Error setting primary organization:', err);
      toast.error('Failed to update primary organization');
      return false;
    }
  }, [fetchOrganizations]);

  // Create new organization
  const createOrganization = useCallback(async (data) => {
    try {
      const response = await organizationAPI.createOrganization(data);

      // API returns { success, data: { organization } } so we access response.data.data.organization
      const orgData = response.data?.data || response.data;

      // Fetch updated organizations list
      const orgsResponse = await organizationAPI.getUserOrganizations();
      const orgsResponseData = orgsResponse.data?.data || orgsResponse.data;
      const orgs = orgsResponseData?.organizations || [];
      setOrganizations(orgs);

      // Switch to the new organization directly using fresh data
      if (orgData?.organization) {
        const newOrg = orgs.find(o => o.id === orgData.organization.id);
        if (newOrg) {
          setCurrentOrganization(newOrg);
          setMembership(newOrg.membership);
          localStorage.setItem('currentOrganizationId', newOrg.id);
        }
      }

      // Check if payment is required for paid tiers
      if (orgData?.paymentRequired) {
        toast.success(`Organization created! ${orgData.paymentInfo?.message || 'Payment required to activate all features.'}`);
      } else {
        toast.success('Organization created successfully');
      }

      return orgData;
    } catch (err) {
      console.error('Error creating organization:', err);
      toast.error(err.response?.data?.error || 'Failed to create organization');
      throw err;
    }
  }, []);

  // Check if user has a specific permission
  const hasPermission = useCallback((permission) => {
    if (!membership) return false;

    const rolePermissions = {
      owner: ['canManageOrganization', 'canManageMembers', 'canManageRoles', 'canManageBilling', 'canDeleteOrganization', 'canViewAnalytics', 'canManageSettings'],
      admin: ['canManageOrganization', 'canManageMembers', 'canManageRoles', 'canViewAnalytics', 'canManageSettings'],
      manager: ['canManageProjects', 'canManageTM', 'canManageGlossaries', 'canViewAnalytics', 'canAssignReviewers'],
      member: ['canCreateProjects', 'canManageTM', 'canManageGlossaries'],
      reviewer: ['canReview']
    };

    const permissions = rolePermissions[membership.role] || [];
    return permissions.includes(permission);
  }, [membership]);

  // Check if feature is available in current tier
  const hasFeature = useCallback((featureKey) => {
    if (!tierInfo) return false;
    return tierInfo.features?.[featureKey] === true;
  }, [tierInfo]);

  // Check usage limits
  const checkUsageLimit = useCallback((limitType) => {
    if (!usage) return { allowed: true, unlimited: true };

    const limitMap = {
      mt_words: usage.mt,
      llm_tokens: usage.llm,
      ai_coaching: usage.aiCoaching,
      storage: usage.storage,
      projects: usage.projects,
      users: usage.users,
      tm_entries: usage.tmEntries,
      glossary_terms: usage.glossaryTerms
    };

    const limitInfo = limitMap[limitType];
    if (!limitInfo) return { allowed: true };

    if (limitInfo.unlimited) {
      return { allowed: true, unlimited: true };
    }

    return {
      allowed: limitInfo.used < limitInfo.limit,
      used: limitInfo.used,
      limit: limitInfo.limit,
      percentage: limitInfo.percentage,
      remaining: limitInfo.limit - limitInfo.used
    };
  }, [usage]);

  // Refresh usage data
  const refreshUsage = useCallback(async () => {
    if (!currentOrganization?.id) return;

    try {
      const response = await organizationAPI.getUsageSummary();
      // API returns { success, data: { usage } }
      const responseData = response.data?.data || response.data;
      setUsage(responseData?.usage);
    } catch (err) {
      console.error('Error refreshing usage:', err);
    }
  }, [currentOrganization?.id]);

  // Initial load
  useEffect(() => {
    if (isAuthenticated) {
      fetchOrganizations();
      return;
    }

    // Auth not resolved yet: hold everything, and above all do NOT touch
    // the persisted organization. This branch used to run on every page
    // load — isAuthenticated is false until the stored token is validated
    // — and cleared currentOrganizationId. By the time auth resolved and
    // fetchOrganizations() read that key it was gone, so the restore fell
    // through to the primary org. Switching to a non-primary org and
    // pressing F5 silently put you back in the primary one, which for a
    // multi-tenant app means acting in the wrong organization.
    if (authLoading) return;

    // Genuinely signed out — now it is correct to forget the selection,
    // so the next user to sign in on this browser does not inherit it.
    setOrganizations([]);
    setCurrentOrganization(null);
    setMembership(null);
    setUsage(null);
    setTierInfo(null);
    setLoading(false);
    localStorage.removeItem('currentOrganizationId');
  }, [isAuthenticated, authLoading, fetchOrganizations]);

  // Fetch details when current organization changes
  useEffect(() => {
    if (currentOrganization?.id) {
      fetchOrganizationDetails();
    }
  }, [currentOrganization?.id, fetchOrganizationDetails]);

  // Set organization header for API calls
  useEffect(() => {
    if (currentOrganization?.id) {
      // This will be picked up by axios interceptor
      localStorage.setItem('currentOrganizationId', currentOrganization.id);
    }
  }, [currentOrganization?.id]);

  const value = {
    // State
    currentOrganization,
    organizations,
    membership,
    usage,
    tierInfo,
    loading,
    error,

    // Actions
    fetchOrganizations,
    fetchOrganizationDetails,
    switchOrganization,
    setPrimaryOrganization,
    createOrganization,
    refreshUsage,

    // Permission checks
    hasPermission,
    hasFeature,
    checkUsageLimit,

    // Computed
    isOwner: membership?.role === 'owner',
    isAdmin: membership?.role === 'admin' || membership?.role === 'owner',
    isManager: ['owner', 'admin', 'manager'].includes(membership?.role),
    tier: currentOrganization?.tier || 'free',
    organizationId: currentOrganization?.id
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export default OrganizationContext;
